import { isSameDay } from 'date-fns';
import { useRef } from 'react';

import { ScenarioHooks, useDebounce } from '../../../../../hooks/index';
import { ScenarioType } from '../../../../../types/scenario-modeling.types';
import { toBackendDateFormat } from '../../../../../utils/to-backend-date-format.util';
import { toNumber } from '../../../../../utils/to-number.util';
import { useScenarioCapTableStore } from '../../../cap-table.store';
import { valuationFormSchema } from '../../scenario-details.validation';

export type UseFundraisingCreationProps = {
  scenarioId: string;
  valuation:
    | {
        date?: Date | undefined;
        preMoneyValuation?: number | undefined;
        type?: ScenarioType | undefined;
      }
    | undefined;
};

type NormalizedValuation = {
  date?: Date;
  preMoneyValuation?: number;
};

const normalizePreMoneyValuation = (value: unknown) => {
  if (value === null || value === undefined || value === '') return undefined;
  return toNumber(value as null | number | string | undefined);
};

const normalizeValuation = (
  value: UseFundraisingCreationProps['valuation'],
): NormalizedValuation => ({
  preMoneyValuation: normalizePreMoneyValuation(value?.preMoneyValuation),
  date: value?.date ? new Date(value.date) : undefined,
});

const isSameValuation = (a: NormalizedValuation, b: NormalizedValuation) =>
  a.preMoneyValuation === b.preMoneyValuation &&
  isSameDay(a.date || new Date(0), b.date || new Date(0));

export const useFundraisingCreation = ({ scenarioId, valuation }: UseFundraisingCreationProps) => {
  const scheduleUpdate = useScenarioCapTableStore(({ scheduleUpdate }) => scheduleUpdate);
  const { update, isPending } = ScenarioHooks.useUpdate();
  const { scenario } = ScenarioHooks.useScenario({ scenarioId });

  // The debounced form watcher also fires when the form is reset from a scenario refetch
  // (e.g. right after "Generate cap table" invalidates GET_SCENARIO). Diff against SERVER
  // state instead of a first-call snapshot: an echo PATCH is not a no-op — it wipes the
  // generated cap table via ClearCalculationInterceptor (blank cap-table QA bug, 2026-07-30).
  const serverValuationRef = useRef<NormalizedValuation | null>(null);
  serverValuationRef.current = scenario
    ? {
        preMoneyValuation: normalizePreMoneyValuation(scenario.preMoneyValuation),
        date: scenario.closingDate ? new Date(scenario.closingDate) : undefined,
      }
    : null;

  const lastSentValuationRef = useRef<NormalizedValuation | null>(null);

  const { debounceCallback: debounceValuationCallback } = useDebounce<typeof valuation>({
    debounce: 1000,
    callback: (val) => {
      const { success } = valuationFormSchema.safeParse(val);
      if (!success) return;

      const serverValuation = serverValuationRef.current;
      if (!serverValuation) return;

      const normalized = normalizeValuation(val);

      // An entirely empty valuation means the form has not hydrated yet — never a user edit.
      if (normalized.preMoneyValuation === undefined && normalized.date === undefined) return;

      if (isSameValuation(serverValuation, normalized)) return;
      if (lastSentValuationRef.current && isSameValuation(lastSentValuationRef.current, normalized))
        return;

      scheduleUpdate(scenarioId);
      update({
        scenarioId,
        data: {
          preMoneyValuation: normalized.preMoneyValuation,
          closingDate: normalized.date ? toBackendDateFormat(normalized.date) : undefined,
        },
      });
      lastSentValuationRef.current = normalized;
    },
  });
  return { debounceValuationCallback, isPending };
};
